// Context
import { useAuth } from 'routes/Auth/AuthProvider';

// Types
import { RegisForm } from './SignUp.types';

// Components
import TextField from 'components/General/TextField/TextField';
import SocialButton from 'components/General/SocialButton/SocialButton';
import Button from 'components/General/Button/Button';

// React hook form
import { useForm, Controller } from 'react-hook-form';

// Layouts
import AuthenticationLayout from 'layouts/AuthenticationLayout/AuthenticationLayout';

// Stylesheet
import './SignUp.scss';

// Navigation
import { Link, useNavigate } from 'react-router-dom';
import { SIGNUP_VERIFY, FORGOT_PASSWORD, PRIVACY } from 'routes/CONSTANTS';

// React cookies
import { useCookies } from 'react-cookie';

// Social login
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import GoogleLogin from 'react-google-login';
import { AxiosError } from 'axios';
import { useState } from 'react';

const SignUp = () => {
  // eslint-disable-next-line
  const [_, setCookie] = useCookies(['email']);
  const [authError, setAuthError] = useState<string>();
  const [generalError, setGeneralError] = useState<string>();
  const navigate = useNavigate();
  const { signup, socialAuth, isAuthenticating } = useAuth();

  const { control, handleSubmit, formState: { errors } } = useForm<RegisForm>({
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = handleSubmit((data) => {
    if (errors.email?.message || errors.password?.message) return;
    setCookie('email', data.email, { path: '/' });
    signup(data)
      .then(() => {
        navigate(SIGNUP_VERIFY);
      })
      .catch((error: AxiosError | any) => {
        if (error.response?.data.message === 'Unverfied User!') {
          setCookie('email', data.email, { path: '/' });
          navigate(SIGNUP_VERIFY);
        } else if (error.response.data.statusCode) {
          setAuthError(' ');
          setGeneralError(error.response.data?.message);
        }
      });
  });

  return (
    <AuthenticationLayout>
      <div className="form-container">
        <h1 className="auth-title">Sign up</h1>
        <form onSubmit={onSubmit}>
          <Controller
            control={control}
            name="email"
            rules={{
              required: 'This field is required',
              minLength: {
                value: 5,
                message: 'Email should be longer than 5 characters'
              },
              maxLength: {
                value: 30,
                message: 'Email cannot be longer than 30 characters'
              }
            }}
            render={({ field: { onChange } }) => (
              <TextField
                variant="rounded"
                type="text"
                placeholder="User@email.com"
                id="email"
                onChange={onChange}
                label="Email"
                error={errors?.email?.message || authError}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            rules={{
              required: true,
              // pattern: {
              //   value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]$/,
              //   message: 'Password should have Uppercase and number characters'
              // },
              minLength: {
                value: 5,
                message: 'Password should be longer than 5 characters'
              },
              maxLength: {
                value: 20,
                message: 'Password cannot be longer than 20 characters'
              }
            }}
            render={({ field: { onChange } }) => (
              <TextField
                variant="rounded"
                type="password"
                placeholder="*******"
                id="password"
                onChange={onChange}
                label="New Password"
                error={errors?.password?.message || generalError || authError}
              />
            )}
          />
          <div className="social-buttons-container">
            <h4>Or Sign In With</h4>
            <div className="social-buttons">
              <FacebookLogin
                appId={process.env.REACT_APP_FACEBOOK_ID || ''}
                fields="name,email,picture"
                callback={(res: any) => socialAuth({ authType: 'facebook', token: res.accessToken })}
                render={(renderProps: any) => (
                  <SocialButton
                    {...renderProps}
                    disabled={isAuthenticating}
                  >
                    Facebook
                  </SocialButton>
                )}
              />
              <GoogleLogin
                clientId={process.env.REACT_APP_GOOGLE_ID || ''}
                render={(renderProps) => (
                  <SocialButton
                    {...renderProps}
                    disabled={isAuthenticating}
                  >
                    Google
                  </SocialButton>
                )}
                buttonText="Login"
                onSuccess={(res: any) => {
                  socialAuth({ authType: 'google', token: res.accessToken });
                }}
                onFailure={(err) => console.log(err)}
                cookiePolicy="single_host_origin"
              />
            </div>
          </div>
          <div className="signup-button">
            <h4>
              Forgot Your Password?
              <span><Link to={FORGOT_PASSWORD}> Reset</Link></span>
            </h4>
            <h4 className="privacy-policy pb-4">
              By signing up, you agree to our
              <a href={`${PRIVACY}`}> Privacy Policy.</a>
            </h4>
            <Button
              color="primary"
              type="submit"
              isLoading={isAuthenticating}
            >
              Sign up
            </Button>
          </div>
        </form>
      </div>
    </AuthenticationLayout>
  );
};
export default SignUp;
